// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "./Checkbox"
import { formatKcal, useI18n } from "@/i18n"
import type { Blocker } from "./types"

interface BlockerScreenProps {
  blocker: NonNullable<Blocker>
  onDismiss: () => void
  onJumpToGoal: () => void
  onUseFloor: (source: "calculated" | "manual") => void
  onAcknowledgeBreastfeedingLoss: () => void
}

/**
 * Full-screen correction/ineligibility surface. ui-ux-spec.md: "Unsafe or
 * infeasible target combinations use a clear correction flow, not a dead
 * end" — every branch here offers a concrete next action, never a bare
 * refusal. Age/ED-history ineligibility (ADR-013 Decision 3) and pregnancy
 * + weight-loss unsafe-goal blocking (ADR-013) never reach the server: the
 * onboarding step is simply not submitted while a blocker is active.
 */
export function BlockerScreen({
  blocker,
  onDismiss,
  onJumpToGoal,
  onUseFloor,
  onAcknowledgeBreastfeedingLoss,
}: BlockerScreenProps) {
  const { t, locale } = useI18n()
  const [ackFloor, setAckFloor] = useState(false)
  const [ackBreastfeedingLoss, setAckBreastfeedingLoss] = useState(false)

  if (blocker.kind === "ineligible_age" || blocker.kind === "ineligible_ed_history") {
    return (
      <section
        role="alert"
        aria-labelledby="ineligible-title"
        className="mx-auto flex w-full max-w-xl flex-col gap-4 rounded-bb-xl border border-line bg-paper p-6 text-center"
      >
        <h1 id="ineligible-title" className="text-xl font-bold text-ink">
          {t("onboarding.ineligible.title")}
        </h1>
        <p className="text-sm text-ink-2">
          {blocker.kind === "ineligible_age"
            ? t("onboarding.ineligible.ageBody")
            : t("onboarding.ineligible.edBody")}
        </p>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <a
            href="mailto:hello@bitebalance.de"
            className="min-h-11 rounded-md border border-line px-4 py-2 text-sm font-medium text-ink hover:border-green/60"
          >
            {t("onboarding.ineligible.contactCta")}
          </a>
          <Button type="button" variant="ghost" className="min-h-11" onClick={onDismiss}>
            {t("onboarding.ineligible.backToStart")}
          </Button>
        </div>
      </section>
    )
  }

  if (blocker.kind === "unsafe_goal_pregnancy") {
    return (
      <section
        role="alert"
        aria-labelledby="pregnancy-conflict-title"
        className="mx-auto flex w-full max-w-xl flex-col gap-4 rounded-bb-xl border border-line bg-paper p-6 text-center"
      >
        <h1 id="pregnancy-conflict-title" className="text-xl font-bold text-ink">
          {t("onboarding.correction.pregnancyGoalTitle")}
        </h1>
        <p className="text-sm text-ink-2">{t("onboarding.correction.pregnancyGoalBody")}</p>
        <div className="flex justify-center">
          <Button type="button" className="min-h-11" onClick={onJumpToGoal}>
            {t("onboarding.correction.updateGoalCta")}
          </Button>
        </div>
      </section>
    )
  }

  if (blocker.kind === "breastfeeding_loss_ack") {
    // 2026-08-28 owner redesign: this is a CARING moment, not an error —
    // warm framed card, the three protections as scannable points with
    // friendly icons, and the acknowledgement as a tappable highlighted
    // tile instead of a bare checkbox row. Copy meaning is unchanged
    // (claims-policy reviewed); only the presentation is.
    const points = [
      {
        emoji: "🔥",
        title: t("onboarding.correction.breastfeedingLossPoint1Title"),
        body: t("onboarding.correction.breastfeedingLossPoint1Body"),
      },
      {
        emoji: "🛡️",
        title: t("onboarding.correction.breastfeedingLossPoint2Title"),
        body: t("onboarding.correction.breastfeedingLossPoint2Body"),
      },
      {
        emoji: "🩺",
        title: t("onboarding.correction.breastfeedingLossPoint3Title"),
        body: t("onboarding.correction.breastfeedingLossPoint3Body"),
      },
    ]
    return (
      <section
        aria-labelledby="breastfeeding-loss-title"
        className="mx-auto flex w-full max-w-xl flex-col gap-5 rounded-bb-xl border-2 border-green/30 bg-paper p-6 md:p-8"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden="true"
            className="flex size-14 items-center justify-center rounded-full bg-green/10 text-3xl"
          >
            🤱
          </span>
          <h1 id="breastfeeding-loss-title" className="text-xl font-bold text-ink md:text-2xl">
            {t("onboarding.correction.breastfeedingLossTitle")}
          </h1>
          <p className="text-sm text-ink-2">{t("onboarding.correction.breastfeedingLossBody")}</p>
        </div>

        <ul className="flex flex-col gap-3">
          {points.map(point => (
            <li key={point.title} className="flex gap-3 rounded-bb-lg bg-cream p-4">
              <span aria-hidden="true" className="text-xl leading-none">
                {point.emoji}
              </span>
              <div className="flex flex-col gap-0.5">
                <p className="text-sm font-semibold text-ink">{point.title}</p>
                <p className="text-sm leading-relaxed text-ink-2">{point.body}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* The acknowledgement is a whole tappable tile — a much bigger,
            friendlier target than a bare checkbox — and turns green when
            confirmed so the state is unmistakable. */}
        <label
          htmlFor="ack-breastfeeding-loss"
          className={`flex cursor-pointer items-start gap-3 rounded-bb-lg border-2 p-4 text-left text-sm transition-colors ${
            ackBreastfeedingLoss
              ? "border-green bg-green/5 text-ink"
              : "border-line bg-paper text-ink-2 hover:border-green/40"
          }`}
        >
          <Checkbox
            checked={ackBreastfeedingLoss}
            onCheckedChange={setAckBreastfeedingLoss}
            id="ack-breastfeeding-loss"
            className="mt-0.5"
          />
          <span>{t("onboarding.correction.acknowledgeBreastfeedingLossLabel")}</span>
        </label>

        <Button
          type="button"
          className="min-h-12 w-full text-base"
          disabled={!ackBreastfeedingLoss}
          onClick={onAcknowledgeBreastfeedingLoss}
        >
          {t("onboarding.correction.breastfeedingLossContinueCta")}
        </Button>
      </section>
    )
  }

  // below_floor_target
  return (
    <section
      role="alert"
      aria-labelledby="below-floor-title"
      className="mx-auto flex w-full max-w-xl flex-col gap-4 rounded-bb-xl border border-line bg-paper p-6 text-center"
    >
      <h1 id="below-floor-title" className="text-xl font-bold text-ink">
        {t("onboarding.correction.belowFloorTitle")}
      </h1>
      <p className="text-sm text-ink-2">
        {t("onboarding.correction.belowFloorBody", {
          target: formatKcal(blocker.targetKcal, locale),
          floor: formatKcal(blocker.floorKcal, locale),
        })}
      </p>
      <label className="mx-auto flex max-w-sm items-start gap-2 text-left text-sm text-ink">
        <Checkbox checked={ackFloor} onCheckedChange={setAckFloor} id="ack-floor" />
        <span>{t("onboarding.correction.acknowledgeFloorLabel")}</span>
      </label>
      <div className="flex justify-center">
        <Button
          type="button"
          className="min-h-11"
          disabled={!ackFloor}
          onClick={() => onUseFloor("calculated")}
        >
          {t("onboarding.correction.useFloorCta")}
        </Button>
      </div>
    </section>
  )
}
